import { useCompanyDetailWorkflowsContextValue } from 'pages/Company/CompanyDetail/context/CompanyDetailWorkflowProvider'
import React from 'react'
import { useEditPaymentMethodFlowContextValue } from '../context/EditPaymentMethodFlowProvider'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  Modal,
  SwitchRoot,
  SwitchThumb,
  toast,
} from '@workwhile/ui'
import { useCompanyDetailContextValue } from '../../../context/CompanyDetailProvider'
import { useController, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  EditPaymentMethodFlowSchema,
  editPaymentMethodFlowSchema,
} from '../schemas/editPaymentMethodFlow'

export const EditPaymentMethodFlowMain = () => {
  const {
    editPaymentMethodFlow: { isOpen, close },
  } = useCompanyDetailWorkflowsContextValue()

  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submitFormToServer },
  } = useEditPaymentMethodFlowContextValue()

  const {
    state: { paymentMethod, invoiceDueDays },
  } = useCompanyDetailContextValue()

  const chargeUpfront = paymentMethod !== 'invoice'
  const isOnInvoice = !chargeUpfront

  const methods = useForm<EditPaymentMethodFlowSchema>({
    resolver: zodResolver(editPaymentMethodFlowSchema),
    values: {
      chargeUpfront,
      invoiceDueDays: isOnInvoice ? invoiceDueDays : undefined,
      currentValues: {
        chargeUpfront,
        invoiceDueDays: isOnInvoice ? invoiceDueDays : undefined,
      },
    },
  })

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = methods

  const { field: chargeUpfrontField } = useController({
    control,
    name: 'chargeUpfront',
  })
  const watchedChargeUpfront = useWatch({ control, name: 'chargeUpfront' })

  const onSubmit = handleSubmit((data) => {
    submitFormToServer(
      {
        chargeUpfront: data.chargeUpfront,
        invoiceDueDays: data.chargeUpfront ? undefined : data.invoiceDueDays,
      },
      {
        onSuccess: () => {
          toast.success('Updated payment method successfully!', {
            position: 'top-center',
          })
          reset()
          close()
        },
      }
    )
  })

  return (
    <Modal
      open={isOpen}
      onClose={close}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Save
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Edit payment method
        </Heading>

        <Flex flexDirection="row" gap={3}>
          <Field
            label="Charge upfront"
            error={errors.chargeUpfront?.message?.toString()}
            // Intentionally disabled. This should work, unsure why it's throwing an error
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            mt={3}
            width="50%"
          >
            <SwitchRoot
              checked={chargeUpfrontField.value}
              onCheckedChange={(value) => chargeUpfrontField.onChange(value)}
              aria-label="Charge upfront"
            >
              <SwitchThumb>
                {chargeUpfrontField.value ? 'Yes' : 'No'}
              </SwitchThumb>
            </SwitchRoot>
          </Field>

          {!watchedChargeUpfront ? (
            <Field
              label="Invoice terms (days)"
              error={errors.invoiceDueDays?.message?.toString()}
              // Intentionally disabled. This should work, unsure why it's throwing an error
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              mt={3}
              width="50%"
            >
              <Input
                {...register('invoiceDueDays')}
                aria-label="Invoice terms"
                placeholder="e.g. 7 days"
              />
            </Field>
          ) : null}
        </Flex>

        {errors.currentValues ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errors.currentValues.message}
          </Message>
        ) : null}
        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </Modal>
  )
}
